import Button from "@components/common/Button";

import CustomAccordion from "@components/common/CustomAccordion";
import StaticSelectFilter from "@components/common/Filters/StaticSelectFilter";
import ListTable from "@components/common/ListTable";
import Pagination from "@components/common/pagination";
import SearchInput from "@components/common/SearchInput";
import FileCsvIcon from "@components/icons/FileCsvIcon";
import PlusIcon from "@components/icons/PlusIcon";
import { ModalTokenVoucher } from "@components/Modals/ModalTokenVoucher";
import { useIsDesktop, useIsMobile, useIsTablet } from "@components/mediaQuery/useIsMobile";
import { fetchVoucherTokens, setDownloadVoucherTokensData } from "@services/vesselService";
import { setVoucherTokensData } from "@store/modules/vesselsSlice";
import { Tooltip } from "flowbite-react";
import { debounce } from "lodash";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const VoucherToken = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const controller = new AbortController();
	const signal = controller.signal;
	const { token } = useSelector((state) => state.auth.user);
	const { t } = useTranslation("vessels");
	const vesselData = useSelector((state) => state.modules.vessels?.vesselDetails);

	const [openAccordions, setOpenAccordions] = useState({
		"voucher-token": true,
	});

	const filterOptions = vesselData?.captive_portals || [];

	const defaultOption = filterOptions[0];

	const [isVoucher, setIsVoucher] = useState(defaultOption);

	const [searchTerms, setSearchTerms] = useState({
		search: "",
	});
	const [isLoadingDownload, setIsLoadingDownload] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null); // Permissions

	useEffect(() => {
		if (filterOptions?.length > 0 && !isVoucher) {
			setIsVoucher(filterOptions[0]);
		}
	}, [filterOptions]);

	const [tokenModalData, setTokenModalData] = useState({
		open: false,
		vesselId: null,
		vesselName: null,
	});

	const {
		loading,
		data: voucherTokensData,
		pagination,
	} = useSelector((state) => state.modules.vessels.voucherTokens);

	const { page, perPage, totalItems, totalPage: totalPages } = pagination;
	const [currentPage, setCurrentPage] = useState(1);
	const [loadingVouchers, setLoadingVouchers] = useState(false);

	const isMobile = useIsMobile();
	const isTablet = useIsTablet();
	const isDesktop = useIsDesktop();

	const vesselDetails = useSelector((state) => state.modules.vessels?.vesselDetails || {});

	const cPortal = isVoucher;
	const setcPortal = setIsVoucher;

	useEffect(() => {
		setcPortal(isVoucher);
	}, [isVoucher]);

	const toggleAccordion = (id) => {
		setOpenAccordions((prevState) => ({
			...prevState,
			[id]: !prevState[id],
		}));
	};

	const loadVoucherTokenData = async (signal) => {
		setLoadingVouchers(true);

		try {
			const result = await fetchVoucherTokens(
				token,
				isVoucher.id,
				dispatch,
				navigate,
				currentPage,
				perPage,
				searchTerms,
				1,
				signal,
			);

			dispatch(setVoucherTokensData(result));
			dispatch(setLoadingVouchers(false));
		} catch (error) {
			if (error.name === "CanceledError") return;
			console.error("Failed to load vouchers:", error);
		} finally {
			setLoadingVouchers(false);
		}
	};

	const debouncedFetch = useMemo(
		() =>
			debounce((signal) => {
				loadVoucherTokenData(signal);
			}, 300),
		[token, isVoucher?.id, currentPage, perPage, searchTerms],
	);

	useEffect(() => {
		const controller = new AbortController();

		debouncedFetch(controller.signal);

		return () => {
			debouncedFetch.cancel();
			controller.abort();
		};
	}, [debouncedFetch]);

	const columns = useMemo(
		() => [
			{
				header: "Token",
				field: "token",
				sortable: false,
			},
			{
				header: "quota (MB)",
				field: "bandwidth_quota",
				sortable: false,
			},
			{
				header: "Consumed",
				field: "consumed",
				sortable: false,
			},
			{
				header: "Remaining",
				field: "remaining",
				sortable: false,
			},
		],
		[isMobile, isTablet],
	);

	const handleRowClick = (id, name) => {
		null;
	};
	const handlePageChange = (newPage) => {
		if (newPage !== currentPage && newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
		}
	};

	const startItemIndex = (currentPage - 1) * perPage + 1;
	const endItemIndex = Math.min(currentPage * perPage, totalItems);
	const today = new Date();

	const handleSearchChange = (id, value) => {
		setSearchTerms((prevTerms) => {
			const updatedTerms = {
				...prevTerms,
				search: value,
			};
			setCurrentPage(1);

			return updatedTerms;
		});
	};

	const handleClearSearch = (id) => {
		setSearchTerms((prev) => ({
			...prev,
			[id]: "",
		}));

		setCurrentPage(1);
	};

	const handleVoucherChange = (value) => {
		setIsVoucher(value);
		setcPortal(value);

		setCurrentPage(1);

		setSearchTerms((prev) => ({
			...prev,
			filter: value?.id,
		}));
	};

	const handleAddToken = (vesselId, vesselName) => {
		setTokenModalData({
			open: true,
			vesselId,
			vesselName,
		});
	};
	const fetchConfigurations = async () => {
		setCurrentPage(1);

		loadVoucherTokenData(signal);
	};

	const handleDownloadVoucherTokens = async () => {
		if (isLoadingDownload) return;

		setIsLoadingDownload(true);
		setIsSuccess(false);
		setHasError(false);
		setErrorMessage(null);

		try {
			await setDownloadVoucherTokensData(
				token,
				dispatch,
				navigate,
				isVoucher?.id,
				searchTerms,
				signal,
			);
			setIsSuccess(true);
		} catch (error) {
			console.error("Download vouchers tokens failed:", error);
			setHasError(true);
			setErrorMessage(error?.message || "Download failed");
		} finally {
			setIsLoadingDownload(false);
		}
		setTimeout(() => {
			setIsSuccess(false);
			setHasError(false);
		}, 1500);
	};

	return (
		<div className="grid grid-cols-1 gap-2 md:grid-cols-1">
			{loading ? (
				<div className="flex h-64 items-center justify-center">
					<div className="h-32 w-32 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
				</div>
			) : true ? (
				<CustomAccordion
					title={
						<div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
							<div className="min-w-[160px] flex-1 sm:w-64 sm:flex-none">
								<StaticSelectFilter
									value={isVoucher}
									options={filterOptions}
									onChange={handleVoucherChange}
									showSearch={false}
									optionLabel="cp_name"
									optionValue="id"
								/>
							</div>
						</div>
					}
					id="vouchersCustomAccordion"
					isOpen={openAccordions[`voucher-token`]}
					toggleAccordion={() => toggleAccordion(`voucher-token`)}
					classCss="border-0 "
					showButton={true}
					customButton={
						<div
							className="flex w-full flex-nowrap items-center justify-center gap-2"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="shrink-0">
								<Button
									text="Add Token"
									color="blue"
									icon={PlusIcon}
									position="before"
									onClick={(e) => {
										e.stopPropagation();
										handleAddToken();
									}}
								/>
							</div>
						</div>
					}
				>
					{/* Right side */}
					<div className="!bg-navbar-bg mt-2 flex flex-col gap-3 p-3 md:flex-row md:items-end md:justify-end md:rounded-lg">
						<div className="w-full md:w-[280px]">
							<SearchInput
								id="search"
								placeholder="Search"
								label="Search by"
								value={searchTerms.search}
								onChange={handleSearchChange}
								onClear={handleClearSearch}
							/>
						</div>
						<div className="flex items-center justify-end">
							<button
								type="button"
								onClick={handleDownloadVoucherTokens}
								disabled={isLoadingDownload || voucherTokensData?.length === 0}
								className={`mt-4 flex items-center justify-center rounded-xl bg-blue-gray px-3 py-2 font-medium text-sm text-white transition-all duration-300 ease-in-out hover:bg-opacity-40 ${
									isLoadingDownload || voucherTokensData?.length === 0
										? "cursor-not-allowed opacity-50"
										: ""
								}`}
							>
								<Tooltip
									content={
										hasError ? errorMessage : isSuccess ? "Downloaded!" : "Export Token List"
									}
									className="!bg-dark-blue !bg-opacity-80 m-0 p-1 text-[11px]"
								>
									<div className="relative flex h-5 w-5 items-center justify-center">
										{/* Spinner */}
										<div
											className={`absolute transition-all duration-300 ${
												isLoadingDownload ? "scale-100 opacity-100" : "scale-50 opacity-0"
											}`}
										>
											<div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
										</div>

										{/* Success */}
										<div
											className={`absolute transition-all duration-300 ${
												isSuccess ? "scale-100 opacity-100" : "scale-50 opacity-0"
											}`}
										>
											✓
										</div>

										{/* Error */}
										<div
											className={`absolute text-red-400 transition-all duration-300 ${
												hasError ? "scale-100 opacity-100" : "scale-50 opacity-0"
											}`}
										>
											✕
										</div>

										{/* Default icon */}
										<div
											className={`transition-all duration-300 ${
												!isLoadingDownload && !isSuccess && !hasError
													? "scale-100 opacity-100"
													: "scale-50 opacity-0"
											}`}
										>
											<FileCsvIcon className="h-5 w-5 text-white" />
										</div>
									</div>
								</Tooltip>
							</button>
						</div>
					</div>
					{loadingVouchers ? (
						<div className="flex h-64 items-center justify-center">
							<div className="h-16 w-16 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
						</div>
					) : (
						<div className="relative mt-2 overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
							<ListTable
								list={voucherTokensData}
								columns={columns}
								isAction={false}
								handleRowClick={handleRowClick}
								loading={loading}
							/>
							<div className="flex flex-col items-center justify-between gap-2 bg-dark-blue bg-opacity-40 px-2 pt-4 pb-4 text-blue-gray text-xs sm:flex-row sm:gap-0 sm:px-4 sm:text-sm">
								<span>
									Showing {startItemIndex} to {endItemIndex} of {totalItems} entries
								</span>
								<Pagination
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={handlePageChange}
								/>
							</div>
						</div>
					)}
				</CustomAccordion>
			) : (
				<div className="flex h-64 items-center justify-center">
					<span className="font-normal text-gray-400 text-xs leading-tight sm:text-sm">
						No data available
					</span>
				</div>
			)}
			{tokenModalData.open && (
				<Suspense fallback={<div></div>}>
					<ModalTokenVoucher
						isOpen={tokenModalData.open}
						closeModal={() => setTokenModalData({ open: false, vesselId: null, vesselName: null })}
						vesselId={vesselData?.id}
						vesselName={vesselData?.site_name}
						token={token}
						fetchConfigurations={fetchConfigurations}
						cPortal={cPortal}
						setcPortal={setIsVoucher}
						handleCaptivePortal={setIsVoucher}
					/>
				</Suspense>
			)}
		</div>
	);
};

export default VoucherToken;
